/**
 * pages/SharedFormLanding.tsx
 *
 * Public landing page for shareable form links (/f/:shareToken).
 * When a client opens a shareable link, this page:
 * 1. Validates the share_token against the shared_links table
 * 2. Checks localStorage for an existing session (so returning visitors
 *    resume their form instead of creating a new one)
 * 3. Creates a new independent request from the template
 * 4. Redirects to /c/:accessToken for the form-filling experience
 *
 * Each visitor gets their own isolated request -- multiple clients can
 * open the same link without any data conflicts.
 *
 * Updated Apr 2026: added captureError import from errorReporting; replaced raw
 *   shareToken in error metadata with hasShareToken boolean to prevent token leakage
 *   in Sentry.
 * Updated Apr 2026: sender branding (logo + display name) resolved and displayed
 *   during loading and on error screens. useDynamicDocumentMeta updates the
 *   browser tab title and favicon.
 * Updated Apr 2026: loading screen shows branding when displayName is available
 *   even if logoUrl is absent, matching the error screen behaviour.
 * Updated Apr 2026: replaced two-query approach (shared_links + public.users)
 *   with a single get_shared_link_with_branding() SECURITY DEFINER RPC. The RPC
 *   bypasses RLS on public.users (which has no anon SELECT policy) while exposing
 *   only logo_url, first_name, last_name alongside the link metadata. setBranding
 *   is called before createRequestFromTemplate so the sender's identity always
 *   appears on the error screen when the share_token is valid.
 * Updated Apr 2026: shareToken effect resets error and branding to INITIAL_BRANDING
 *   before each fetch so stale UI from a previous token cannot bleed through when
 *   the param changes mid-session (e.g. user pastes a new /f/:shareToken URL).
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { createRequestFromTemplate } from '../utils/createRequestFromTemplate';
import Spinner from '../components/ui/Spinner';
import Card from '../components/ui/Card';
import ClientBrandingHeader from '../components/branding/ClientBrandingHeader';
import { getSenderDisplayName } from '../utils/brandingHelpers';
import { useDynamicDocumentMeta } from '../hooks/useDynamicDocumentMeta';
import { Link2Off } from 'lucide-react';
import { captureError } from '../utils/errorReporting';

const SESSION_KEY_PREFIX = 'qollect_shared_session_';

interface Branding {
  logoUrl: string | null;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
}

const INITIAL_BRANDING: Branding = {
  logoUrl: null,
  displayName: null,
  firstName: null,
  lastName: null,
};

export default function SharedFormLanding() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [branding, setBranding] = useState<Branding>(INITIAL_BRANDING);

  useDynamicDocumentMeta({
    title: branding.displayName ? `Questionnaire - ${branding.displayName}` : 'Questionnaire',
    faviconUrl: branding.logoUrl,
  });

  const initForm = useCallback(async (token: string) => {
    const sessionKey = `${SESSION_KEY_PREFIX}${token}`;
    const existingAccessToken = localStorage.getItem(sessionKey);

    if (existingAccessToken) {
      const { data: existingRequest, error: validateError } = await supabase
        .from('requests')
        .select('id')
        .eq('access_token', existingAccessToken)
        .maybeSingle();

      if (!validateError && existingRequest) {
        navigate(`/c/${existingAccessToken}`, { replace: true });
        return;
      }

      localStorage.removeItem(sessionKey);
    }

    try {
      // Single RPC resolves both link metadata and sender branding in one round-trip.
      // SECURITY DEFINER function bypasses RLS on public.users (no anon policy)
      // while exposing only the three branding columns - nothing else from users.
      const { data: rows, error: linkError } = await supabase
        .rpc('get_shared_link_with_branding', { p_share_token: token });

      if (linkError) throw linkError;

      const sharedLink = rows?.[0] ?? null;

      if (!sharedLink) {
        setError('Ce lien de formulaire est introuvable.');
        return;
      }

      // Set branding before any downstream call that might throw so the
      // sender's identity is visible on the error screen.
      if (sharedLink.logo_url || sharedLink.first_name || sharedLink.last_name) {
        const displayName = getSenderDisplayName({
          first_name: sharedLink.first_name,
          last_name: sharedLink.last_name,
        });
        setBranding({
          logoUrl: sharedLink.logo_url,
          displayName,
          firstName: sharedLink.first_name,
          lastName: sharedLink.last_name,
        });
      }

      if (!sharedLink.is_active) {
        setError('Ce lien de formulaire n\'est plus actif.');
        return;
      }

      const { accessToken } = await createRequestFromTemplate({
        templateId: sharedLink.template_id,
        userId: sharedLink.user_id,
        clientId: null,
        sharedLinkId: sharedLink.id,
      });

      localStorage.setItem(sessionKey, accessToken);

      navigate(`/c/${accessToken}`, { replace: true });
    } catch (err) {
      console.error('Error initializing shared form:', err);
      captureError(err, { page: 'SharedFormLanding', action: 'initForm', hasShareToken: !!token });
      setError('Une erreur est survenue lors de la préparation du formulaire. Veuillez réessayer.');
    }
  }, [navigate]);

  useEffect(() => {
    setError(null);
    setBranding(INITIAL_BRANDING);
    if (!shareToken) {
      setError('Lien invalide.');
      return;
    }
    initForm(shareToken);
  }, [shareToken, initForm]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <div className="text-center py-8">
            {(branding.logoUrl || branding.displayName) && (
              <div className="flex justify-center mb-6">
                <ClientBrandingHeader
                  logoUrl={branding.logoUrl}
                  displayName={branding.displayName}
                  firstName={branding.firstName}
                  lastName={branding.lastName}
                  size="md"
                />
              </div>
            )}
            <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Link2Off className="w-8 h-8 text-error-600" />
            </div>
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">Lien inaccessible</h2>
            <p className="text-neutral-600 text-sm">{error}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="text-center">
        {(branding.logoUrl || branding.displayName) && (
          <div className="flex justify-center mb-6">
            <ClientBrandingHeader
              logoUrl={branding.logoUrl}
              displayName={branding.displayName}
              firstName={branding.firstName}
              lastName={branding.lastName}
              size="md"
            />
          </div>
        )}
        <div className="text-primary-600 mb-4 flex justify-center">
          <Spinner size="md" />
        </div>
        <p className="text-neutral-600">Préparation de votre formulaire...</p>
      </div>
    </div>
  );
}
